/**
 * 管理画面ダッシュボード
 * 学生: 自身の面接数・平均スコア・スコア推移チャート
 * 教育者: 担当学生の統計
 * 管理者: システム全体の統計
 */

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { getDashboardStats } from '../../lib/admin-api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Users,
  UserCircle,
  Calendar,
  MessageSquare,
  Loader2,
  TrendingUp,
  ChevronRight,
  Clock3,
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { cn } from '../../lib/utils';
import { DEMO_TREND_WEEK_OPTIONS, getPublicTeacherDashboard, teacherHref } from '../../lib/demo-teacher';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

type StudentDashboardStats = {
  scope: 'student';
  interviews: { total: number };
  scores: { average: number };
  scoreTrend: Array<{
    index: number;
    interviewId: number;
    score: number;
    scoreType: string;
    created: string;
  }>;
};

type StaffDashboardStats = {
  scope: 'staff';
  users: { total: number; byRole: Record<string, number> };
  activeSessions: number;
  interviews: { total: number };
  scores: { average: number };
};

type EducatorDashboardStats = {
  scope: 'educator';
  selectedGrade: number | null;
  students: {
    total: number;
    active: number;
    activeRate: number;
    activeWindowDays: number;
  };
  interviews: { total: number };
  scores: { evaluated: number; average: number };
  usage: {
    totalSeconds: number;
    averagePerInterviewSeconds: number;
    interviewCount: number;
    basis: 'first_to_last_round';
  };
  weeklyScoreTrend: Array<{
    weekStart: string;
    average: number | null;
    evaluatedCount: number;
  }>;
  classes: Array<{
    id: number;
    name: string;
    academicYear: number | null;
    grade: number | null;
    section: string | null;
    studentCount: number;
    activeStudentCount: number;
    interviewCount: number;
    averageScore: number | null;
    weakestCategory: {
      name: string;
      achievementRate: number;
      evaluatedItemCount: number;
    } | null;
  }>;
};

function isStudentStats(d: unknown): d is StudentDashboardStats {
  if (typeof d !== 'object' || d === null) return false;
  const scope = (d as { scope?: unknown }).scope;
  return scope === 'student';
}

function isEducatorStats(d: unknown): d is EducatorDashboardStats {
  if (typeof d !== 'object' || d === null) return false;
  return (d as { scope?: unknown }).scope === 'educator';
}

/** API の型ゆれ（camelCase / snake_case）と数値の文字列化に耐える */
function normalizeStudentScoreTrend(raw: unknown): StudentDashboardStats['scoreTrend'] {
  if (!raw || typeof raw !== 'object') return [];
  const o = raw as Record<string, unknown>;
  const list = (o.scoreTrend ?? o.score_trend) as unknown;
  if (!Array.isArray(list)) return [];
  return list.map((row, i) => {
    const r = row as Record<string, unknown>;
    const scoreRaw = r.score ?? r.score_value;
    const score = typeof scoreRaw === 'number' ? scoreRaw : parseFloat(String(scoreRaw ?? 'NaN'));
    return {
      index: typeof r.index === 'number' ? r.index : parseInt(String(r.index ?? i + 1), 10) || i + 1,
      interviewId: typeof r.interviewId === 'number' ? r.interviewId : parseInt(String(r.interview_id ?? r.interviewId ?? 0), 10),
      score: Number.isFinite(score) ? score : 0,
      scoreType: String(r.scoreType ?? r.score_type ?? ''),
      created: typeof r.created === 'string' ? r.created : String(r.created ?? ''),
    };
  });
}

export default function Dashboard() {
  const [grade, setGrade] = useState<number | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'dashboard', 'stats', grade],
    queryFn: () => getDashboardStats(grade),
  });

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-medical-blue" />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="text-red-600">エラーが発生しました: {error instanceof Error ? error.message : '不明なエラー'}</div>
      </AdminLayout>
    );
  }

  if (isStudentStats(data)) {
    return (
      <StudentDashboard
        stats={{
          ...data,
          scoreTrend: normalizeStudentScoreTrend(data),
        }}
      />
    );
  }

  if (isEducatorStats(data)) {
    return <EducatorDashboard stats={data} grade={grade} onGradeChange={setGrade} />;
  }

  const stats = (data || {
    scope: 'staff' as const,
    users: { total: 0, byRole: {} },
    activeSessions: 0,
    interviews: { total: 0 },
    scores: { average: 0 },
  }) as StaffDashboardStats;

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-gray-900">ダッシュボード</h1>
          <p className="text-gray-500 mt-2 text-base">システム全体の統計情報</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <DashboardMetricCard
            label="ユーザー数"
            value={stats.users.total}
            icon={<Users className="h-7 w-7" />}
            sub={`管理者: ${stats.users.byRole?.admin || 0} / 教育者: ${stats.users.byRole?.educator || 0} / 学生: ${stats.users.byRole?.student || 0}`}
          />
          <DashboardMetricCard
            label="アクティブセッション"
            value={stats.activeSessions}
            icon={<UserCircle className="h-7 w-7" />}
            sub="現在アクティブなセッション数"
          />
          <DashboardMetricCard
            label="面接数"
            value={stats.interviews.total}
            icon={<Calendar className="h-7 w-7" />}
            sub="実施された面接の総数"
          />
          <DashboardMetricCard
            label="平均スコア"
            value={stats.scores.average ? stats.scores.average.toFixed(1) : '0.0'}
            icon={<MessageSquare className="h-7 w-7" />}
            sub="全スコアの平均値"
          />
        </div>

        <div className="max-w-lg rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-4">システム情報</div>
          <div className="divide-y divide-gray-100">
            <SystemInfoRow label="DB接続" status="ok" />
            <SystemInfoRow label="API" status="ok" />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

/** 教員画面の公開デモ（/demo/teacher）のダッシュボード */
export function DemoTeacherDashboard() {
  const [grade, setGrade] = useState<number | null>(null);
  const [trendWeeks, setTrendWeeks] = useState<number>(12);
  const { data, isLoading, error } = useQuery({
    queryKey: ['public', 'teacher-demo', 'dashboard', grade, trendWeeks],
    queryFn: () => getPublicTeacherDashboard(grade, trendWeeks),
    placeholderData: (previous) => previous,
  });

  if (isLoading || error || !isEducatorStats(data)) {
    return (
      <AdminLayout demo>
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-medical-blue" />
          </div>
        ) : (
          <div className="text-red-600">デモのダッシュボードを読み込めませんでした。</div>
        )}
      </AdminLayout>
    );
  }
  return (
    <EducatorDashboard
      stats={data}
      grade={grade}
      onGradeChange={setGrade}
      trendWeeks={trendWeeks}
      onTrendWeeksChange={setTrendWeeks}
      demo
    />
  );
}

function EducatorDashboard({
  stats,
  grade,
  onGradeChange,
  trendWeeks = 8,
  onTrendWeeksChange,
  demo = false,
}: {
  stats: EducatorDashboardStats;
  grade: number | null;
  onGradeChange: (grade: number | null) => void;
  trendWeeks?: number;
  onTrendWeeksChange?: (weeks: number) => void;
  demo?: boolean;
}) {
  const weeklyTrend = stats.weeklyScoreTrend.map((item) => ({
    ...item,
    label: new Date(item.weekStart).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' }),
  }));
  const hasWeeklyScores = weeklyTrend.some((item) => item.average != null);

  return (
    <AdminLayout demo={demo}>
      <div className="space-y-8">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-gray-900">ダッシュボード</h1>
            <p className="mt-2 text-base text-gray-500">担当学生の学習状況</p>
          </div>
          <label className="flex w-fit items-center gap-2 text-sm font-bold text-gray-700">
            学年
            <select
              value={grade ?? ''}
              onChange={(event) => onGradeChange(event.target.value ? Number(event.target.value) : null)}
              className="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm font-normal text-gray-900"
              aria-label="ダッシュボードを学年で絞り込み"
            >
              <option value="">全学年</option>
              <option value="1">1年生</option>
              <option value="2">2年生</option>
              <option value="3">3年生</option>
            </select>
          </label>
        </header>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <DashboardMetricCard
            label="担当学生"
            value={stats.students.total}
            icon={<Users className="h-7 w-7" />}
            sub="現在割り当てられている学生"
          />
          <DashboardMetricCard
            label="2週間アクティブ数"
            value={`${stats.students.active}人`}
            icon={<UserCircle className="h-7 w-7" />}
            sub={`直近${stats.students.activeWindowDays}日: 担当${stats.students.total}人中 ${(
              Number(stats.students.activeRate || 0) * 100
            ).toFixed(1)}%`}
          />
          <DashboardMetricCard
            label="面接数"
            value={stats.interviews.total}
            icon={<Calendar className="h-7 w-7" />}
            sub="担当学生が開始した面接"
          />
          <DashboardMetricCard
            label="評価済み面接"
            value={stats.scores.evaluated}
            icon={<MessageSquare className="h-7 w-7" />}
            sub="採点結果が保存された面接"
          />
          <DashboardMetricCard
            label="平均スコア"
            value={Number(stats.scores.average || 0).toFixed(1)}
            icon={<TrendingUp className="h-7 w-7" />}
            sub="担当学生の最新採点値の平均"
          />
          <DashboardMetricCard
            label="平均利用時間"
            value={formatDuration(stats.usage.averagePerInterviewSeconds)}
            icon={<Clock3 className="h-7 w-7" />}
            sub="面接1回あたりの推定会話時間"
          />
          <DashboardMetricCard
            label="総利用時間"
            value={formatDuration(stats.usage.totalSeconds)}
            icon={<Clock3 className="h-7 w-7" />}
            sub="担当学生の推定会話時間の合計"
          />
        </div>

        <section className="border-t border-gray-200 pt-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">期間中の平均点推移</h2>
              <p className="mt-1 text-sm text-gray-500">直近{trendWeeks}週間の採点結果を1週間ごとに平均しています。</p>
            </div>
            {onTrendWeeksChange && (
              <div className="inline-flex w-fit rounded-lg border border-gray-200 bg-white p-1" aria-label="推移の期間">
                {DEMO_TREND_WEEK_OPTIONS.map((weeks) => (
                  <button
                    key={weeks}
                    type="button"
                    onClick={() => onTrendWeeksChange(weeks)}
                    className={cn(
                      'min-w-20 rounded-md px-3 py-2 text-sm font-bold transition-colors',
                      trendWeeks === weeks ? 'bg-cyan-800 text-white' : 'text-gray-600 hover:bg-gray-100'
                    )}
                  >
                    {weeks}週
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="mt-4 h-72 w-full rounded-lg border border-gray-200 bg-white p-4">
            {hasWeeklyScores ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weeklyTrend} margin={{ top: 12, right: 18, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} width={42} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value: number | string) => [`${Number(value).toFixed(1)}点`, '週平均']}
                    labelFormatter={(label) => `${label}の週`}
                  />
                  <Line
                    type="linear"
                    dataKey="average"
                    stroke="#0e7490"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    connectNulls={false}
                    name="週平均"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                直近{trendWeeks}週間の採点結果がありません。
              </div>
            )}
          </div>
        </section>

        <section className="border-t border-gray-200 pt-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">クラス別の学習状況</h2>
            <p className="mt-1 text-sm text-gray-500">
              各クラスの利用状況と、患者面接の評価項目で達成率が最も低いカテゴリを表示します。
            </p>
          </div>
          <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] border-collapse text-left">
                <thead className="bg-gray-50 text-sm text-gray-600">
                  <tr>
                    <th className="px-4 py-3 font-bold">クラス</th>
                    <th className="px-4 py-3 font-bold">学生数</th>
                    <th className="px-4 py-3 font-bold">アクティブ</th>
                    <th className="px-4 py-3 font-bold">面接数</th>
                    <th className="px-4 py-3 font-bold">平均スコア</th>
                    <th className="px-4 py-3 font-bold">弱点カテゴリ</th>
                    <th className="w-12 px-2 py-3"><span className="sr-only">クラス詳細</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stats.classes.map((educatorClass) => (
                    <tr key={educatorClass.id} className="text-sm text-gray-700 hover:bg-cyan-50">
                      <td className="px-4 py-3">
                        <div className="font-bold text-gray-900">{educatorClass.name}</div>
                        <div className="mt-0.5 text-xs text-gray-500">
                          {educatorClass.academicYear ? `${educatorClass.academicYear}年度` : '年度未設定'}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium">{educatorClass.studentCount}名</td>
                      <td className="px-4 py-3 font-medium">{educatorClass.activeStudentCount}名</td>
                      <td className="px-4 py-3 font-medium">{educatorClass.interviewCount}回</td>
                      <td className="px-4 py-3 font-bold text-gray-900">
                        {educatorClass.averageScore == null ? '-' : `${educatorClass.averageScore.toFixed(1)}点`}
                      </td>
                      <td className="px-4 py-3">
                        {educatorClass.weakestCategory ? (
                          <div>
                            <div className="font-bold text-gray-900">{educatorClass.weakestCategory.name}</div>
                            <div className="mt-0.5 text-xs text-gray-500">
                              達成率 {(educatorClass.weakestCategory.achievementRate * 100).toFixed(1)}%
                            </div>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-2 py-3">
                        <Link href={teacherHref(demo, `/teacher/classes/${educatorClass.id}`)}>
                          <a
                            href={teacherHref(demo, `/teacher/classes/${educatorClass.id}`)}
                            aria-label={`${educatorClass.name}の詳細`}
                            className="grid h-9 w-9 place-items-center rounded-md text-gray-500 hover:bg-cyan-100 hover:text-cyan-900"
                          >
                            <ChevronRight className="h-5 w-5" />
                          </a>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {stats.classes.length === 0 && (
              <div className="px-6 py-12 text-center text-sm text-gray-500">担当クラスがありません。</div>
            )}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}

function formatDuration(value: number): string {
  const seconds = Math.max(0, Math.round(Number(value) || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

function DashboardMetricCard({
  label,
  value,
  icon,
  sub,
}: {
  label: string;
  value: number | string;
  icon: ReactNode;
  sub: string;
}) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="text-base font-bold text-gray-700">{label}</div>
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-cyan-50 text-cyan-600">
          {icon}
        </div>
      </div>
      <div className="mt-4 text-5xl font-extrabold tracking-tight text-gray-900 tabular-nums">
        {value}
      </div>
      <div className="mt-3 text-base text-gray-600 leading-relaxed">{sub}</div>
    </div>
  );
}

function SystemInfoRow({ label, status }: { label: string; status: 'ok' | 'warn' | 'down' }) {
  const meta =
    status === 'ok'
      ? { text: '正常', cls: 'bg-green-50 text-green-700' }
      : status === 'warn'
        ? { text: '警告', cls: 'bg-amber-50 text-amber-700' }
        : { text: '異常', cls: 'bg-red-50 text-red-700' };
  return (
    <div className="flex items-center justify-between py-4">
      <span className="text-base font-bold text-gray-900">{label}</span>
      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-base font-bold ${meta.cls}`}>
        {status === 'ok' && <span aria-hidden>✓</span>}
        {meta.text}
      </span>
    </div>
  );
}

function StudentDashboard({ stats }: { stats: StudentDashboardStats }) {
  const trend = Array.isArray(stats.scoreTrend) ? stats.scoreTrend : [];
  const chartRows = trend.map((d) => ({
    ...d,
    label: `${d.index}件目`,
  }));

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">ダッシュボード</h1>
          <p className="text-gray-600 mt-2">あなたの面接数・スコアの状況</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">面接数</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.interviews.total}</div>
              <p className="text-xs text-muted-foreground mt-1">あなたが実施した面接の件数</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">平均スコア</CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {Number(stats.scores.average ?? 0).toFixed(1)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                総合スコアの到達率の平均
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-muted-foreground" />
                スコアの推移
              </CardTitle>
              <p className="text-sm font-normal text-muted-foreground mt-1">
                面接×種別（patient / dentist）ごとの最新スコアを、計測日時の古い順に並べています（再スコアの古い行は除外）。
              </p>
            </div>
          </CardHeader>
          <CardContent>
            {chartRows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                まだスコア記録がありません。面接を実施するとここに推移が表示されます。
              </p>
            ) : (
              <div className="w-full min-w-0" style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={chartRows} margin={{ top: 12, right: 16, left: 8, bottom: 28 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="index"
                      type="number"
                      allowDecimals={false}
                      tick={{ fontSize: 12 }}
                      label={{ value: '記録順（件目）', position: 'insideBottom', offset: -12, fontSize: 11 }}
                    />
                    <YAxis
                      dataKey="score"
                      tick={{ fontSize: 12 }}
                      domain={['auto', 'auto']}
                      width={44}
                      label={{ value: 'スコア', angle: -90, position: 'insideLeft', fontSize: 11 }}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0].payload as (typeof chartRows)[0];
                        const when = p.created
                          ? new Date(p.created).toLocaleString('ja-JP')
                          : '-';
                        return (
                          <div className="rounded-md border bg-white px-3 py-2 text-sm shadow-md">
                            <div className="font-medium">スコア: {Number(p.score).toFixed(1)}</div>
                            <div className="text-muted-foreground text-xs mt-1">
                              種別:{' '}
                              {p.scoreType === 'patient'
                                ? '患者面接'
                                : p.scoreType === 'dentist'
                                  ? '歯科医報告'
                                  : p.scoreType || '-'}
                            </div>
                            <div className="text-muted-foreground text-xs">面接 ID: {p.interviewId}</div>
                            <div className="text-muted-foreground text-xs">{when}</div>
                          </div>
                        );
                      }}
                    />
                    <Line
                      type="linear"
                      dataKey="score"
                      stroke="hsl(213, 78%, 54%)"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name="スコア"
                      isAnimationActive={chartRows.length < 200}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
